import { useEffect } from "react";

import {
  IResourceComponentsProps,
  useShow,
  useNotification,
  useCreate,
  useNavigation,
} from "@refinedev/core";
import { Show } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";

import {
  GlobalStyles,
  Grid,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
} from "@mui/material";

import { MuiFileInput } from "mui-file-input";
import { Controller } from "react-hook-form";

import { commonValues } from "constants/common.values";
import { theme } from "constants/theme";

import { checkLocation } from "utils/location";

import {
  FileEntity,
  CreateAttendanceDto,
  FieldEntityFileTypeEnum,
} from "generated";

type DefaultHouseRecordFormValues = {
  image: File | null;
  identifier: string;
  description: string;
  location: number[];
};
const defaultHouseRecordFormValues: DefaultHouseRecordFormValues = {
  image: null,
  identifier: "",
  description: "",
  location: [],
};

export const UnAvailableHouseRecordForm: React.FC<
  IResourceComponentsProps
> = () => {
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: defaultHouseRecordFormValues,
  });

  const { open } = useNotification();

  const { list } = useNavigation();

  const { mutate, isLoading: isCreating, mutateAsync } = useCreate();

  const showErrorMessage = (message: string) => {
    open?.({
      type: "error",
      message,
    });
  };

  const uploadImage = async (file: File) => {
    let hasError = false;

    const formData = new FormData();
    formData.append("files", file);

    try {
      const response = (
        await mutateAsync({
          resource: "files",
          values: formData,
        })
      ).data as FileEntity[] | undefined;

      if (response?.length && response[0].url) {
        open?.({
          type: "success",
          message: "Image upload complete",
        });
        return response[0].url;
      }

      hasError = true;
    } catch (error) {
      hasError = true;
    }

    if (hasError) {
      open?.({
        type: "error",
        message: "Error uploading image",
      });
    }

    return null;
  };

  const onSubmit = async (formData: DefaultHouseRecordFormValues) => {
    let imageData: string | null = null;

    if (!formData.location.length) {
      open?.({
        type: "error",
        message: "Location not found",
      });
    }

    if (formData.image) {
      const res = await uploadImage(formData.image);
      if (!res) {
        return;
      }

      imageData = res;
    }

    const attendanceData: CreateAttendanceDto = {
      formId: (data?.data?.id as string) || "",
      image: imageData || undefined,
      identifier: formData.identifier.trim(),
      description: formData.description.trim(),
      location: formData.location,
    };
    mutate(
      {
        resource: "attendances",
        values: attendanceData,
        successNotification: () => {
          return {
            description: "Record collected",
            message: `A record successfully created.`,
            type: "success",
          };
        },
        errorNotification: (errors) => {
          return {
            description: errors?.message,
            message: errors?.errors?.length
              ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
              : "",
            type: "error",
          };
        },
      },
      {
        onSuccess: () => list("forms"),
      }
    );
  };

  useEffect(() => {
    checkLocation({
      onSuccess(data) {
        setValue("location", [data.coords.longitude, data.coords.latitude]);
      },
    });
  }, [setValue]);

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            overscrollBehaviorY: "contain",
          },
        }}
      />

      <Show
        headerButtons={<></>}
        title={"पारिवारिक रेकर्ड"}
        isLoading={isLoading}
      >
        {data?.data?.id ? (
          <Grid
            marginX={"auto"}
            border={theme.borderPrimary}
            borderRadius={theme.borderRadius}
            p={theme.commonPadding}
            container
            item
            md={10}
          >
            <Box
              component={"form"}
              onSubmit={handleSubmit(onSubmit)}
              display={"flex"}
              flexDirection={"column"}
              flex={1}
              gap={theme.gap}
            >
              <Typography>पहिचानकर्ता / घर नम्बर</Typography>

              <FormControl variant="standard" fullWidth>
                <Controller
                  name="identifier"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={"पहिचानकर्ता / घर नम्बर"}
                      {...field}
                      placeholder="घरको लागि पहिचानकर्ता वा घर नम्बर थप्नुहोस्"
                    />
                  )}
                />
              </FormControl>

              <Typography>विवरण</Typography>

              <FormControl variant="standard" fullWidth>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={"विवरण"}
                      {...field}
                      placeholder="रेकर्ड बारे विवरण थप्नुहोस्"
                    />
                  )}
                />
              </FormControl>

              <Typography>फोटो *</Typography>
              <FormControl variant="standard" fullWidth>
                <Controller
                  name={"image"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { ref, ...field }, fieldState }) => {
                    return (
                      <MuiFileInput
                        {...field}
                        id={"image"}
                        label={"फोटो *"}
                        inputRef={ref}
                        helperText={fieldState.invalid ? "फोटो आवश्यक छ" : ""}
                        error={fieldState.invalid}
                        placeholder={"घरको फोटो थप्नुहोस् *"}
                        onChange={(file) => {
                          if (!file) {
                            field.onChange(file);
                            return;
                          }

                          const hasInvalidType = !file.type.includes(
                            FieldEntityFileTypeEnum.Image
                          );

                          if (hasInvalidType) {
                            showErrorMessage("Inavlid file type found");
                            return;
                          }

                          const hasExceededSize =
                            file.size / (1024 * 1024) >
                            commonValues.maxFileSize;

                          if (hasExceededSize) {
                            showErrorMessage(
                              `Max file size is ${commonValues.maxFileSize} MB`
                            );
                            return;
                          }

                          field.onChange(file);
                        }}
                      />
                    );
                  }}
                />
              </FormControl>

              <Typography>स्थान *</Typography>

              <FormControl variant="standard" fullWidth>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      required
                      label={"स्थान *"}
                      placeholder="स्थान थप्नुहोस् *"
                    />
                  )}
                />
              </FormControl>

              <Button
                variant="contained"
                style={{ letterSpacing: 3 }}
                disabled={isCreating}
                type="submit"
              >
                CREATE
              </Button>
            </Box>
          </Grid>
        ) : (
          <></>
        )}
      </Show>
    </>
  );
};
